/**
 * EditSellerDialog - Dialog para editar/atribuir vendedor ao cliente
 * Permite selecionar um vendedor da lista ou remover atribuição
 * 
 * @version 1.0.0
 * RELEASE: 2026-01-10
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SellerSelector } from '@/components/crm/SellerSelector';
import { useCrmCustomers } from '@/hooks/useCrmCustomers';
import { Loader2 } from 'lucide-react';
import { createLogger } from '@/lib/logger';

// Logger para depuração
const logger = createLogger('EditSellerDialog');

// Props do componente
interface EditSellerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: string;
  currentSellerId?: string | null;
  onSuccess?: () => void;
}

/**
 * Dialog de edição de vendedor responsável
 */
export function EditSellerDialog({
  open,
  onOpenChange,
  customerId,
  currentSellerId,
  onSuccess
}: EditSellerDialogProps) {
  // Estado local para o vendedor selecionado
  const [sellerId, setSellerId] = useState<string | null>(currentSellerId || null);
  
  // Hook de atualização do cliente
  const { updateCustomer } = useCrmCustomers();

  // Sincroniza estado quando dialog abre
  useEffect(() => {
    if (open) {
      setSellerId(currentSellerId || null);
    }
  }, [open, currentSellerId]);

  /**
   * Handler para salvar alteração
   */
  const handleSave = async () => {
    logger.debug('[EditSellerDialog] Salvando vendedor:', { customerId, sellerId });

    try {
      await updateCustomer.mutateAsync({
        id: customerId,
        seller_id: sellerId
      });
      
      logger.info('[EditSellerDialog] Vendedor atualizado com sucesso');
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      logger.error('[EditSellerDialog] Erro ao atualizar vendedor:', error);
    }
  };

  const isLoading = updateCustomer.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Vendedor Responsável</DialogTitle>
          <DialogDescription>
            Selecione o vendedor responsável por este cliente.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <SellerSelector
            value={sellerId || undefined}
            onSelect={(id) => setSellerId(id || null)}
            placeholder="Selecione um vendedor"
            allowClear
          />
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={isLoading}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
